<template>
  <div v-if="inline && options?.length" class="w-full flex flex-col bg-base" :v-bind="$attrs">
    <span class="flex items-center gap-2 px-6 font-size-4">
      <span>{{ name }}</span>
      <span v-if="activeFilterValues?.length" class="cursor-pointer underline font-size-3" @click="onClearSelection">
        {{ t('canvasUI.components.filters.clear') }}
      </span>
    </span>
    <div class="mb-2 py-2 px-6">
      <div v-for="option in options" :key="option.value" class="w-full h-8 flex items-center">
        <NCheckbox
          :checked="isOptionChecked(option.value)"
          class="px-0 truncate"
          :disabled="disabled"
          :onUpdate:checked="(v) => onOptionSelect(option, v)"
          :value="option.value"
        >
          {{ option.label }}
        </NCheckbox>
      </div>
    </div>
  </div>
  <NPopover
    v-else-if="options?.length"
    class="c-select-filter"
    :on-clickoutside="onOutsideClick"
    placement="bottom-start"
    raw
    :show="isOpen"
    :show-arrow="false"
    trigger="manual"
  >
    <template #trigger>
      <NTag
        class="select-none cursor-pointer"
        :style="{
          '--n-color': activeFilterValues?.length ? themeVars?.primary5 : themeVars?.primary6,
          '--n-border': `1px solid ${themeVars?.primary5}`,
          '--n-height': '28px',
        }"
        :themeOverrides="{
          borderRadius: '4px',
        }"
        @click="isOpen = !isOpen"
      >
        <div class="flex items-center">
          <div v-if="!activeFilterValues.length">
            {{ filterName }}
          </div>
          <div v-else class="font-bold">
            {{ `${filterName}:` }}
          </div>
          <div v-if="activeFilterValues.length" class="ml-1">
            {{ activeFilterValues[0]?.label }}
          </div>
          <div
            v-if="activeFilterValues.length > 1"
            class="ml-1 px-[6px] rounded h-[22px] flex items-center justify-center"
            :style="{ background: themeVars.primary, color: themeVars.base }"
          >
            {{ `+${activeFilterValues.length - 1}` }}
          </div>
        </div>
      </NTag>
    </template>
    <div class="c-select-filter__popover w-[350px] rounded flex flex-col bg-base" :v-bind="$attrs">
      <div class="pa-2 max-h-[300px] overflow-y-auto">
        <div v-for="option in options" :key="option.value" class="w-full h-8 flex items-center">
          <NCheckbox
            :checked="isOptionChecked(option.value)"
            class="px-2 truncate"
            :disabled="disabled"
            :onUpdate:checked="(v) => onOptionSelect(option, v)"
            :value="option.value"
          >
            {{ option.label }}
          </NCheckbox>
        </div>
      </div>
      <div class="pa-2" :style="{ borderTop: `1px solid ${themeVars.primary5}` }">
        <CButton block class="pa-2" :disabled="disabled" text @click="onClearSelection">
          {{ t('canvasUI.components.filters.clearSelection') }}
        </CButton>
      </div>

      <div
        v-if="slots.footer"
        class="c-select-filter__footer pa-4"
        :style="{ borderTop: `1px solid ${themeVars.primary5}` }"
      >
        <slot name="footer" />
      </div>
    </div>
  </NPopover>
</template>

<script lang="ts" setup generic="FilterValue extends string">
import { computed, ref, useSlots } from 'vue'
import { NCheckbox, NTag, NPopover } from 'naive-ui'
import { useI18n } from 'vue-i18n'

import CButton from '@lib/components/CButton/CButton.vue'
import useThemeVars from '@lib/composables/useThemeVars'

type Option = {
  label: string
  value: FilterValue
}

type Props = {
  disabled?: boolean
  name?: string
  modelValue: FilterValue[] | FilterValue | null | ''
  inline?: boolean
  options: Option[]
  multiple?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  inline: false,
  name: '',
  multiple: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', v: Props['modelValue']): void
}>()

const slots = useSlots()
const themeVars = useThemeVars()
const { t } = useI18n()
const isOpen = ref(false)
const activeFilterValues = computed(() => {
  let modelValues: FilterValue[]

  if (Array.isArray(props.modelValue)) {
    modelValues = props.modelValue
  } else if (props.modelValue !== null && props.modelValue !== undefined && props.modelValue !== '') {
    if (typeof props.modelValue === 'boolean') {
      modelValues = [String(props.modelValue) as FilterValue]
    } else {
      modelValues = [props.modelValue]
    }
  } else {
    modelValues = []
  }

  const optionsMap = new Map(props.options.map((option) => [option.value, option]))
  return modelValues.map((value) => optionsMap.get(value)).filter(Boolean)
})
const filterName = computed(() => props.name || t('canvasUI.components.filters.selectLabel'))

function onOptionSelect(o: Option, selected: boolean) {
  let current
  let newValue: Props['modelValue']
  if (props.multiple) {
    if (Array.isArray(props.modelValue)) {
      current = props.modelValue as FilterValue[]
    } else {
      current = props.modelValue ? [props.modelValue] : []
    }
    newValue = selected ? [...current, o.value] : current.filter((value) => value !== o.value)
  } else {
    if ((o.value === 'true' || o.value === 'false') && props.modelValue === o.value) {
      newValue = null
    } else {
      newValue = selected ? o.value : null
    }
  }
  emit('update:modelValue', newValue === '' ? null : newValue)
}
function onOutsideClick() {
  setTimeout(() => {
    isOpen.value = false
  }, 0)
}

function onClearSelection() {
  emit('update:modelValue', null)
  isOpen.value = false
}

function isOptionChecked(value: any) {
  if (
    (value === 'true' || value === 'false') &&
    (typeof props.modelValue === 'string' || typeof props.modelValue === 'boolean')
  ) {
    if (typeof props.modelValue === 'boolean') {
      return value === String(props.modelValue)
    }
    return value === props.modelValue
  }

  if (Array.isArray(props.modelValue)) {
    return props.modelValue.includes(value)
  }

  if (typeof props.modelValue === 'boolean') {
    return value === String(props.modelValue)
  }

  return props.modelValue === value
}
</script>

<style scoped lang="scss">
.c-select-filter {
  &__popover {
    box-shadow: 0px 7px 24px 0px rgba(0, 0, 0, 0.1);
  }
}
</style>
